import { useState, useEffect, useMemo } from 'react';
import { useBack } from '../hooks';
import api from '../api';
import { useToast } from '../context/ToastContext';
import WeightResult from '../components/WeightResult';

// The weight calculator on its own (the director, 15 Oct 2026: "a section under
// Invoices"): any parts and products, any quantities -- for a quote, or to book
// a lorry before there is an order. The same arithmetic as the invoice form.

const TEAL = '#14b8a6';
const BORD = '#2a3040';
const CARD = '#1e2329';
const DARK = '#0f1117';
const MUTED = '#6b7280';
const TEXT = '#f0f2f5';

const inp = { background: '#161b22', border: `1px solid ${BORD}`, borderRadius: '8px', padding: '7px 9px', fontSize: '13px', color: TEXT, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'system-ui' };
const btn = (primary) => ({
  background: primary ? TEAL : 'transparent', color: primary ? '#0f172a' : TEAL,
  border: `1px solid ${primary ? TEAL : 'rgba(20,184,166,0.6)'}`, borderRadius: '8px', padding: '7px 14px',
  fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap',
});
const EMPTY = { what: '', quantity: '' };

export default function WeightCalculator() {
  const goBack = useBack('/admin/invoices');
  const { toast } = useToast();
  const [parts, setParts] = useState([]);
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([{ ...EMPTY }, { ...EMPTY }, { ...EMPTY }]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/api/v1/parts').then(r => setParts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/api/v1/products').then(r => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const choosable = useMemo(() => ({
    products: products.filter(p => !p.discontinued).sort((a, b) => a.name.localeCompare(b.name)),
    parts: parts.filter(p => !p.discontinued && p.status !== 'discontinued').sort((a, b) => a.name.localeCompare(b.name)),
  }), [parts, products]);

  const setRow = (i, k, v) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  // `use`: the tape and foils picked in the result; the defaults at first.
  async function workOut(use = result ? result.use : undefined) {
    const lines = rows.filter(r => r.what && Number(String(r.quantity).replace(',', '.')) > 0).map(r => {
      const [kind, id] = r.what.split(':');
      return { [kind === 'part' ? 'part_id' : 'product_id']: kind === 'part' ? Number(id) : id, quantity: Number(String(r.quantity).replace(',', '.')) };
    });
    if (!lines.length) { toast.error('Choose at least one part or product and how many.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/api/v1/invoices/weights', { lines, use });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'The weights could not be worked out just now.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box', maxWidth: '1100px', margin: '0 auto' }}>
      <button onClick={goBack} style={{ background: 'none', border: 'none', color: MUTED, fontSize: '12px', cursor: 'pointer', padding: 0, marginBottom: '8px' }}>
        <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px', marginRight: '4px' }} />Back
      </button>
      <h1 style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 4px' }}>Weight calculator</h1>
      <p style={{ color: MUTED, fontSize: '13px', margin: '0 0 16px' }}>
        Net and gross weight, Euro pallets and pallet spaces for any parts and products — from their weights, packaging and how their pallets stack.
      </p>

      <div style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 140px 34px', gap: '8px', marginBottom: '8px' }}>
            <select style={inp} value={r.what} onChange={e => setRow(i, 'what', e.target.value)}>
              <option value="">Choose a part or product…</option>
              <optgroup label="Products">{choosable.products.map(p => <option key={p.id} value={`product:${p.id}`}>{p.name}{p.code ? ` (${p.code})` : ''}</option>)}</optgroup>
              <optgroup label="Parts">{choosable.parts.map(p => <option key={p.id} value={`part:${p.id}`}>{p.name}{p.part_number ? ` (${p.part_number})` : ''}</option>)}</optgroup>
            </select>
            <input style={{ ...inp, textAlign: 'right' }} placeholder="How many" value={r.quantity} onChange={e => setRow(i, 'quantity', e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') workOut(); }} />
            <button title="Take this row off" onClick={() => setRows(rs => (rs.length > 1 ? rs.filter((_, j) => j !== i) : [{ ...EMPTY }]))}
              style={{ background: 'none', border: `1px solid ${BORD}`, color: '#fca5a5', borderRadius: '6px', cursor: 'pointer' }}>
              <i className="fa-solid fa-xmark" style={{ fontSize: '11px' }} />
            </button>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginTop: '6px' }}>
          <button style={btn(false)} onClick={() => setRows(rs => [...rs, { ...EMPTY }])}><i className="fa-solid fa-plus" style={{ fontSize: '11px', marginRight: '5px' }} />Another row</button>
          <button style={btn(true)} disabled={busy} onClick={() => workOut()}>{busy ? 'Working it out…' : 'Work it out'}</button>
        </div>
      </div>

      {result && (
        <div style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: '12px', padding: '16px' }}>
          <WeightResult result={result} onChoose={use => workOut(use)} />
        </div>
      )}
    </div>
  );
}
